import { useEffect, useRef, useState, type ReactElement } from 'react';
import {
  canPurchase,
  metaBonuses,
  MetaCurrency,
  powerGauge,
  purchase,
  respec,
  skillPointsAvailable,
  type MetaNodeDef,
  type MetaState,
  type MilestoneDef,
} from '../game/meta';
import { HangarPreview, type HangarLoadout } from '../view/HangarPreview';
import './hangar.css';

/* ═══════════════════════════════════════════════════════════════════════════
   L'écran de méta-progression.

   ⚠️ La JAUGE DE PUISSANCE y est affichée en permanence, et ce n'est pas
   décoratif (PLAN.md §6.9). C'est une décision de TRANSPARENCE : le joueur
   sait, dès le premier coup d'œil, qu'il ne pourra pas acheter la victoire.

   Sans elle, un joueur qui bute sur le monde 3 conclut que le jeu est un mur
   de points de vie et qu'il lui manque de la progression — alors qu'il lui
   manque de la maîtrise. Avec elle, le plafond devient un objectif de
   complétion au lieu d'une frustration.

   Les deux monnaies sont affichées SÉPARÉMENT et NOMMÉES, jamais additionnées
   en un « total de progression ». Toute la lisibilité de la troisième monnaie
   repose là-dessus : elles ne sont pas convertibles, donc elles ne doivent
   jamais avoir l'air de l'être.
   ═══════════════════════════════════════════════════════════════════════════ */

export interface MetaScreenProps {
  nodes: readonly MetaNodeDef[];
  milestones: readonly MilestoneDef[];
  state: MetaState;
  onChange: (next: MetaState) => void;
  onBack: () => void;
  /** `null` tant que le nœud correspondant n'est pas acquis. Un bouton absent
   *  vaut mieux qu'un bouton grisé : le hangar contient déjà le nœud qui
   *  l'ouvre, et l'afficher deux fois — une fois à vendre, une fois inerte —
   *  ne renseigne personne. */
  onTraining: (() => void) | null;
  /** L'équipement sélectionné, reflété par l'aperçu 3D. Facultatif : sans lui,
   *  l'aperçu retombe sur la coque et l'arme de série. */
  loadout?: HangarLoadout;
}

/** Équipement de repli quand `loadout` n'est pas fourni : la coque et le canon
 *  de série (`DEFAULT_SHIP_ID` / `DEFAULT_WEAPON_ID`), la secondaire d'office. */
const DEFAULT_LOADOUT: HangarLoadout = {
  shipId: 'vulcan',
  weaponId: 'vulcan',
  secondaryId: 'lance-perforante',
  upgrades: [],
};

export function MetaScreen({
  nodes,
  milestones,
  state,
  onChange,
  onBack,
  onTraining,
  loadout,
}: MetaScreenProps): ReactElement {
  const [message, setMessage] = useState('');

  /* ── L'aperçu 3D ─────────────────────────────────────────────────────────
     Un renderer AUTONOME (`HangarPreview`), monté sur un canvas dédié tant que
     cet écran est visible et démonté avec lui — le canvas de jeu est masqué
     pendant les menus, l'aperçu ne peut donc pas s'y greffer.

     Deux effets distincts, à dessein : le premier gère le CYCLE DE VIE (créer
     au montage, détruire au démontage), le second POUSSE la sélection courante
     sans jamais recréer le contexte. */
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const previewRef = useRef<HangarPreview | null>(null);
  // Optimisme assumé : on suppose WebGL présent, et on bascule en dégradé
  // seulement si la création échoue. La quasi-totalité des appareils l'ont.
  const [previewOk, setPreviewOk] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let preview: HangarPreview;
    try {
      preview = new HangarPreview(canvas);
    } catch {
      // Navigateur sans WebGL : on masque l'aperçu, l'écran reste utilisable.
      setPreviewOk(false);
      return;
    }
    if (!preview.ok) {
      preview.dispose();
      setPreviewOk(false);
      return;
    }
    previewRef.current = preview;
    preview.start();
    return () => {
      // `dispose()` coupe la boucle et rend TOUT le GPU (voir HangarPreview) :
      // rien ne survit à la fermeture de l'écran.
      preview.dispose();
      previewRef.current = null;
    };
  }, []);

  useEffect(() => {
    previewRef.current?.setLoadout(loadout ?? DEFAULT_LOADOUT);
  }, [loadout]);

  const bought = new Set(state.nodes);
  const bonus = metaBonuses(nodes, bought);
  const gauge = powerGauge(bonus);
  const points = skillPointsAvailable(milestones, nodes, state);

  const buy = (node: MetaNodeDef): void => {
    const verdict = canPurchase(node, state, points);
    if (!verdict.ok) {
      setMessage(verdict.reason ?? 'Indisponible.');
      return;
    }
    setMessage('');
    onChange(purchase(node, state));
  };

  const familles = grouper(nodes);
  const [ouverte, setOuverte] = useState<string>(familles[0]?.titre ?? '');

  // Légende de l'aperçu : le vaisseau et l'arme en clair (repli sur l'identifiant
  // quand aucun nom n'est fourni).
  const l = loadout ?? DEFAULT_LOADOUT;
  const caption = `${(l.shipName ?? l.shipId).toUpperCase()} · ${l.weaponName ?? l.weaponId}`;

  return (
    <div className="screen meta">
      <h2>HANGAR</h2>

      <div className="hangar-cols">
        {/* ── Colonne de gauche : l'aperçu et l'état permanent du joueur ──── */}
        <div className="hangar-apercu">
          <div className="hangar-preview">
            {previewOk ? (
              // `aria-hidden` : l'aperçu est décoratif, la sélection réelle se
              // fait au briefing. Un lecteur d'écran n'a rien à y lire.
              <canvas ref={canvasRef} className="hangar-canvas" aria-hidden="true" />
            ) : (
              <div className="hangar-preview-degrade">
                Aperçu 3D indisponible sur cet appareil.
              </div>
            )}
            <span className="hangar-caption">{caption}</span>
          </div>

          {/* Les deux soldes, séparés et nommés. */}
          <div className="wallet">
            <span className="w-skill">{points} pts de compétence</span>
            <span className="w-credits">{state.credits.toLocaleString('fr-FR')} crédits</span>
          </div>

          {/* La jauge, toujours visible. */}
          <div className="gauge">
            <span className="gauge-label">PUISSANCE PERMANENTE</span>
            <div className="gauge-bar">
              <div className="gauge-fill" style={{ transform: `scaleX(${gauge.ratio})` }} />
            </div>
            <span className="gauge-value">
              {(gauge.value * 100).toFixed(0)} % / {(gauge.cap * 100).toFixed(0)} %
            </span>
          </div>
          <p className="hangar-note">
            Le plafond est volontaire : la progression permanente rend le jeu plus confortable,
            jamais gagnable à sa place. Un pilote excellent termine le jeu sans rien acheter.
          </p>
        </div>

        {/* ── Colonne de droite : l'arbre, inchangé dans son fonctionnement ── */}
        <div className="hangar-arbre">
          {message && <p className="meta-msg">{message}</p>}

          {/* ⚠️ Une famille ouverte à la fois. La version précédente empilait les
              trente nœuds dans deux colonnes, et le retour de jeu a été sans appel :
              « ça devient bordélique, tout se mélange ».

              Le problème n'était pas la quantité mais l'absence de familles. Deux
              colonnes séparaient les MONNAIES — information utile une fois, au
              premier coup d'œil — alors que le joueur cherche une CHOSE : une arme,
              un vaisseau, un palier de dégâts. Regrouper par nature et n'en montrer
              qu'une réduit l'écran à une décision à la fois, ce qui est exactement
              ce qu'on lui demande de prendre. */}
          <div className="meta-onglets" role="tablist">
            {familles.map((f) => {
              const acquis = f.nodes.filter((n) => state.nodes.includes(n.id)).length;
              return (
                <button
                  key={f.titre}
                  role="tab"
                  aria-selected={f.titre === ouverte}
                  className={`onglet${f.titre === ouverte ? ' actif' : ''}`}
                  onClick={() => setOuverte(f.titre)}
                >
                  {f.titre}
                  {/* Le compteur acquis/total évite d'avoir à ouvrir chaque famille
                      pour savoir où il reste quelque chose à prendre. */}
                  <span className="onglet-compte">
                    {acquis}/{f.nodes.length}
                  </span>
                </button>
              );
            })}
          </div>

          {familles
            .filter((f) => f.titre === ouverte)
            .map((f) => (
              <NodeColumn
                key={f.titre}
                title={f.titre}
                hint={f.hint}
                nodes={f.nodes}
                state={state}
                points={points}
                onBuy={buy}
              />
            ))}
        </div>
      </div>

      <div className="meta-actions">
        {onTraining && (
          <button className="btn" onClick={onTraining}>
            ENTRAÎNEMENT
          </button>
        )}
        <button
          className="btn"
          onClick={() => {
            // Respec GRATUIT et illimité, comme le plan l'exige. Le rendre
            // payant transformerait l'expérimentation en dépense, et le joueur
            // cesserait d'essayer des choses.
            onChange(respec(nodes, state));
            setMessage('Compétences réinitialisées.');
          }}
        >
          RÉINITIALISER LES COMPÉTENCES
        </button>
        <button className="btn btn-primary" onClick={onBack}>
          RETOUR
        </button>
      </div>
    </div>
  );
}

interface Famille {
  readonly titre: string;
  readonly hint: string;
  readonly nodes: MetaNodeDef[];
}

/**
 * Range les nœuds par NATURE de ce qu'ils apportent, et non par monnaie.
 *
 * ⚠️ L'ordre des familles n'est pas alphabétique : il va du plus structurant au
 * plus accessoire. Un joueur qui ouvre le hangar pour la première fois doit
 * tomber sur les compétences, qui sont la progression principale ; les
 * contraintes volontaires, qui ne concernent qu'un joueur déjà chevronné,
 * viennent en dernier.
 *
 * Une famille vide n'est PAS affichée. Le contenu de l'arbre évolue, et un
 * onglet « 0/0 » qu'on ouvre pour découvrir une page blanche est un défaut
 * qu'on ne remarque qu'en production.
 */
function grouper(nodes: readonly MetaNodeDef[]): Famille[] {
  const skill = nodes.filter((n) => n.currency === MetaCurrency.Skill);
  const opt = (kind: string): MetaNodeDef[] =>
    nodes.filter((n) => n.kind === 'option' && n.option === kind);

  // Les contraintes volontaires se reconnaissent à leur `grants`. C'est fragile
  // — une convention de nommage n'est pas un type — mais l'alternative serait
  // d'ajouter une catégorie au schéma pour un seul écran, ce qui ferait
  // remonter une décision de présentation jusque dans le contenu.
  const estContrainte = (n: MetaNodeDef): boolean =>
    n.kind === 'option' && (n.grants.startsWith('contrainte-') || n.grants === 'cumul-contraintes');

  const familles: Famille[] = [
    {
      titre: 'COMPÉTENCES',
      hint: 'Payées en points de maîtrise — gagnés par des jalons, jamais par le temps passé.',
      nodes: skill,
    },
    {
      titre: 'ARMES',
      hint: 'Payées en crédits. Une arme débloquée se choisit ensuite au briefing, avant chaque partie.',
      nodes: opt('weapon'),
    },
    {
      titre: 'VAISSEAUX',
      hint: 'Payés en crédits. Chacun est un échange, jamais une amélioration.',
      nodes: opt('ship'),
    },
    {
      titre: 'DÉBLOCAGES',
      hint: 'Confort, entraînement et raccourcis. Rien ici ne rend le vaisseau plus puissant.',
      nodes: opt('unlock').filter((n) => !estContrainte(n)),
    },
    {
      titre: 'CONTRAINTES',
      hint: 'Elles RETIRENT de la puissance contre davantage de crédits. Pour qui trouve le jeu trop confortable.',
      nodes: opt('unlock').filter(estContrainte),
    },
  ];
  return familles.filter((f) => f.nodes.length > 0);
}

function NodeColumn({
  title,
  hint,
  nodes,
  state,
  points,
  onBuy,
}: {
  title: string;
  hint: string;
  nodes: readonly MetaNodeDef[];
  state: MetaState;
  points: number;
  onBuy: (n: MetaNodeDef) => void;
}): ReactElement {
  return (
    <div className="meta-col">
      <h3>{title}</h3>
      <p className="hint">{hint}</p>
      {nodes.map((n) => {
        const owned = state.nodes.includes(n.id);
        const verdict = canPurchase(n, state, points);
        return (
          <button
            key={n.id}
            className={`node${owned ? ' owned' : ''}${!owned && !verdict.ok ? ' locked' : ''}`}
            onClick={() => onBuy(n)}
            disabled={owned}
          >
            <span className="node-name">{n.name}</span>
            <span className="node-text">{n.text}</span>
            <span className="node-cost">
              {owned ? 'ACQUIS' : `${n.cost} ${n.currency === MetaCurrency.Skill ? 'pts' : 'cr'}`}
            </span>
          </button>
        );
      })}
    </div>
  );
}
